import { Product } from '@portal/portal-core';

/** Reference database entry exposed to consumers (v1-compatible shape). */
export interface ReferenceDatabase {
  name: string;
  url: string;
}

// Asset category IDs from cloud-core
export const ASSET_CATEGORY_IDS = {
  INSTALLER: 'installer',
  INSTALLATION_INSTRUCTIONS: 'installation-instructions',
  USER_ACCESS_CONTROL_MODULE: 'user-access-control-module',
  REFERENCE_DATABASES: 'reference-databases',
  REFERENCE_DATABASES_IVD: 'reference-databases-ivd',
  REFERENCE_DATABASES_OTHER: 'reference-databases-other',
};

/**
 * Check if assets are in v2 format
 * @param assets - Product assets object
 * @returns True if assets are in v2 format, false otherwise
 */
export const isAssetsV2 = (
  assets: Product.ProductAssets | null | undefined,
): assets is Product.AssetsV2 => {
  return Product.isAssetsV2(assets);
};

/**
 * Get items from a specific category in v2 assets
 * @param assets - Product assets (v2 format)
 * @param categoryId - Category ID to retrieve
 * @returns Array of asset items with url and optional label
 */
export const getCategoryItems = (
  assets: Product.ProductAssets | null | undefined,
  categoryId: string,
): Product.AssetItem[] => {
  if (!isAssetsV2(assets)) {
    return [];
  }

  const category = assets.categories?.find((cat) => cat.id === categoryId);
  return category?.items || [];
};

/**
 * Get installer URL from assets (v1 or v2)
 * @param assets - Product assets
 * @returns Installer URL or null if not found
 */
export const getInstallerUrl = (
  assets: Product.ProductAssets | null | undefined,
): string | null => {
  if (!assets) return null;

  // V2 format
  if (isAssetsV2(assets)) {
    const items = getCategoryItems(assets, ASSET_CATEGORY_IDS.INSTALLER);
    return items[0]?.url || null;
  }

  // V1 format
  return assets.installerUrl || null;
};

/**
 * Get installer label from assets (v1 or v2)
 * @param assets - Product assets
 * @returns Installer display label or null if not found
 */
export const getInstallerLabel = (
  assets: Product.ProductAssets | null | undefined,
): string | null => {
  if (!assets) return null;

  // V2 format
  if (isAssetsV2(assets)) {
    const items = getCategoryItems(assets, ASSET_CATEGORY_IDS.INSTALLER);
    return items[0]?.label || null;
  }

  // V1 format
  return assets.installerUrlLabel || null;
};

/**
 * Get installation instructions URL from assets (v1 or v2)
 * @param assets - Product assets
 * @returns Installation instructions URL or null if not found
 */
export const getInstructionsUrl = (
  assets: Product.ProductAssets | null | undefined,
): string | null => {
  if (!assets) return null;

  // V2 format
  if (isAssetsV2(assets)) {
    const items = getCategoryItems(assets, ASSET_CATEGORY_IDS.INSTALLATION_INSTRUCTIONS);
    return items[0]?.url || null;
  }

  // V1 format
  return assets.instructionsUrl || null;
};

/**
 * Get user access control module URL from assets (v1 or v2)
 * @param assets - Product assets
 * @returns User access control module URL or null if not found
 */
export const getUserAccessControlUrl = (
  assets: Product.ProductAssets | null | undefined,
): string | null => {
  if (!assets) return null;

  // V2 format
  if (isAssetsV2(assets)) {
    const items = getCategoryItems(assets, ASSET_CATEGORY_IDS.USER_ACCESS_CONTROL_MODULE);
    return items[0]?.url || null;
  }

  // V1 format
  return assets.userAccessControlModuleUrl || null;
};

/**
 * Get user access control module label from assets (v1 or v2)
 * @param assets - Product assets
 * @returns User access control module display label or null if not found
 */
export const getUserAccessControlLabel = (
  assets: Product.ProductAssets | null | undefined,
): string | null => {
  if (!assets) return null;

  // V2 format
  if (isAssetsV2(assets)) {
    const items = getCategoryItems(assets, ASSET_CATEGORY_IDS.USER_ACCESS_CONTROL_MODULE);
    return items[0]?.label || null;
  }

  // V1 format
  return assets.userAccessControlModuleUrlLabel || null;
};

/**
 * Get all reference databases from assets (v1 or v2)
 * For v2, returns items from all categories EXCEPT the standard non-database categories
 * (installer, installation-instructions, user-access-control-module)
 * @param assets - Product assets
 * @returns Array of {name, url} objects
 */
export const getReferenceDatabases = (
  assets: Product.ProductAssets | null | undefined,
): ReferenceDatabase[] => {
  if (!assets) return [];

  // V2 format - get items from all categories except standard non-database ones
  if (isAssetsV2(assets)) {
    const allDatabases: Product.AssetItem[] = [];

    // Category IDs to exclude (non-database categories)
    const excludedCategoryIds = [
      ASSET_CATEGORY_IDS.INSTALLER,
      ASSET_CATEGORY_IDS.INSTALLATION_INSTRUCTIONS,
      ASSET_CATEGORY_IDS.USER_ACCESS_CONTROL_MODULE,
    ];

    // Get items from all categories except excluded ones
    // Categories are in user-defined order, so preserve that order
    assets.categories?.forEach((category) => {
      if (!excludedCategoryIds.includes(category.id)) {
        allDatabases.push(...(category.items || []));
      }
    });

    // Convert label to name for backward compatibility
    return allDatabases.map((db) => ({
      name: db.label || '',
      url: db.url,
    }));
  }

  // V1 format
  return assets.referenceDatabases || [];
};

/**
 * Get reference databases by category (v2 only)
 * @param assets - Product assets (v2 format)
 * @param categoryId - Category ID (REFERENCE_DATABASES_IVD or REFERENCE_DATABASES_OTHER)
 * @returns Array of {label, url} objects
 */
export const getReferenceDatabasesByCategory = (
  assets: Product.ProductAssets | null | undefined,
  categoryId: string,
): Product.AssetItem[] => {
  if (!isAssetsV2(assets)) {
    return [];
  }

  return getCategoryItems(assets, categoryId);
};

/**
 * Get all reference database categories (v2 only)
 * Returns categories with their labels and items, preserving user-defined order
 * Excludes standard non-database categories
 * @param assets - Product assets (v2 format)
 * @returns Array of {id, label, items} objects
 */
export const getReferenceDatabaseCategories = (
  assets: Product.ProductAssets | null | undefined,
): Product.AssetCategory[] => {
  if (!isAssetsV2(assets)) {
    return [];
  }

  // Category IDs to exclude (non-database categories)
  const excludedCategoryIds = [
    ASSET_CATEGORY_IDS.INSTALLER,
    ASSET_CATEGORY_IDS.INSTALLATION_INSTRUCTIONS,
    ASSET_CATEGORY_IDS.USER_ACCESS_CONTROL_MODULE,
  ];

  // Return all categories except excluded ones, preserving order
  return (assets.categories || []).filter((category) => !excludedCategoryIds.includes(category.id));
};
